/**
 * Estimation Service - API Client for Project Estimation System
 * Phase 3: SpecKit Integration & Queue System
 * 
 * Provides API methods for both client and admin estimation workflows
 */

import { ApiService } from './api';
import {
  ProjectEstimation,
  EstimationClarification,
  GeneratedSpecification,
  EstimationNote,
  SubmitEstimationRequest,
  AnswerClarificationsRequest,
  ReviewEstimationRequest,
  ApproveEstimationRequest,
  RejectEstimationRequest,
  ConvertToProjectRequest,
  RegenerateSpecificationRequest,
  AddNoteRequest,
  EstimationFilters,
  PaginatedResponse,
  QueueStatusResponse,
  AIProvider,
} from '../types/estimation';

// ============================================================================
// CLIENT ESTIMATION SERVICE
// ============================================================================

export class EstimationService {
  
  // --------------------------------------------------------------------------
  // CLIENT ENDPOINTS
  // --------------------------------------------------------------------------

  /**
   * Submit a new project estimation request
   * @param data Project estimation details
   * @returns Created estimation with initial clarification questions
   */
  static async submitEstimation(data: SubmitEstimationRequest): Promise<{
    estimation: ProjectEstimation;
    clarifications: EstimationClarification[];
    message: string;
  }> {
    try {
      return await ApiService.post('/client/estimations/submit', data);
    } catch (error) {
      console.error('Error submitting estimation:', error);
      throw error;
    }
  }

  /**
   * Get list of estimations for the authenticated client
   * @param filters Optional filters for status, date range, etc.
   * @returns Paginated list of estimations
   */
  static async getClientEstimations(
    filters?: EstimationFilters
  ): Promise<PaginatedResponse<ProjectEstimation>> {
    try {
      const params = new URLSearchParams();
      
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            if (Array.isArray(value)) {
              value.forEach(v => params.append(`${key}[]`, v.toString()));
            } else {
              params.append(key, value.toString());
            }
          }
        });
      }

      const queryString = params.toString();
      const url = `/client/estimations${queryString ? `?${queryString}` : ''}`;
      
      return await ApiService.get(url);
    } catch (error) {
      console.error('Error fetching client estimations:', error);
      throw error;
    }
  }

  /**
   * Get single estimation details for client
   * @param id Estimation ID
   * @returns Estimation with clarifications and progress
   */
  static async getClientEstimation(id: number): Promise<{
    estimation: ProjectEstimation;
    clarifications: EstimationClarification[];
    latest_specification?: GeneratedSpecification;
    progress: {
      clarification_progress: number;
      required_questions: number;
      answered_questions: number;
      can_generate_specification: boolean;
    };
  }> {
    try {
      return await ApiService.get(`/client/estimations/${id}`);
    } catch (error) {
      console.error('Error fetching client estimation:', error);
      throw error;
    }
  }

  /**
   * Answer clarification questions
   * @param id Estimation ID
   * @param data Answers to clarification questions
   * @returns Updated estimation with queue status
   */
  static async answerClarifications(
    id: number,
    data: AnswerClarificationsRequest
  ): Promise<{
    estimation: ProjectEstimation;
    clarifications: EstimationClarification[];
    specification_queued: boolean;
    message: string;
  }> {
    try {
      return await ApiService.post(`/client/estimations/${id}/clarifications/answer`, data);
    } catch (error) {
      console.error('Error answering clarifications:', error);
      throw error;
    }
  }

  /**
   * View generated specification
   * @param id Estimation ID
   * @returns Latest specification with all versions
   */
  static async viewSpecification(id: number): Promise<{
    estimation: ProjectEstimation;
    latest_specification: GeneratedSpecification;
    all_versions: GeneratedSpecification[];
  }> {
    try {
      return await ApiService.get(`/client/estimations/${id}/specification`);
    } catch (error) {
      console.error('Error viewing specification:', error);
      throw error;
    }
  }

  /**
   * Download specification as PDF
   * @param id Estimation ID
   * @returns Blob for PDF download
   */
  static async downloadSpecification(id: number): Promise<Blob> {
    try {
      // TODO: Implement PDF download when backend ready
      throw new Error('PDF download not yet implemented');
    } catch (error) {
      console.error('Error downloading specification:', error);
      throw error;
    }
  }

  // --------------------------------------------------------------------------
  // ADMIN ENDPOINTS
  // --------------------------------------------------------------------------

  /**
   * Get list of all estimations (admin view)
   * @param filters Optional filters for status, client, date range, etc.
   * @returns Paginated list of all estimations with detailed info
   */
  static async getAdminEstimations(
    filters?: EstimationFilters
  ): Promise<PaginatedResponse<ProjectEstimation>> {
    try {
      const params = new URLSearchParams();
      
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            if (Array.isArray(value)) {
              value.forEach(v => params.append(`${key}[]`, v.toString()));
            } else {
              params.append(key, value.toString());
            }
          }
        });
      }

      const queryString = params.toString();
      const url = `/admin/estimations${queryString ? `?${queryString}` : ''}`;
      
      return await ApiService.get(url);
    } catch (error) {
      console.error('Error fetching admin estimations:', error);
      throw error;
    }
  }

  /**
   * Get single estimation details (admin view)
   * @param id Estimation ID
   * @returns Full estimation details including all versions and internal notes
   */
  static async getAdminEstimation(id: number): Promise<{
    estimation: ProjectEstimation;
    clarifications: EstimationClarification[];
    specifications: GeneratedSpecification[];
    latest_specification?: GeneratedSpecification;
    notes: EstimationNote[];
    queue_jobs: any[];
    history: any[];
  }> {
    try {
      return await ApiService.get(`/admin/estimations/${id}`);
    } catch (error) {
      console.error('Error fetching admin estimation:', error);
      throw error;
    }
  }

  /**
   * Add review notes to estimation
   * @param id Estimation ID
   * @param data Review notes
   * @returns Updated estimation
   */
  static async reviewEstimation(
    id: number,
    data: ReviewEstimationRequest
  ): Promise<{
    estimation: ProjectEstimation;
    note: EstimationNote;
    message: string;
  }> {
    try {
      return await ApiService.post(`/admin/estimations/${id}/review`, data);
    } catch (error) {
      console.error('Error reviewing estimation:', error);
      throw error;
    }
  }

  /**
   * Approve estimation
   * @param id Estimation ID
   * @param data Approval notes
   * @returns Approved estimation
   */
  static async approveEstimation(
    id: number,
    data?: ApproveEstimationRequest
  ): Promise<{
    estimation: ProjectEstimation;
    message: string;
  }> {
    try {
      return await ApiService.post(`/admin/estimations/${id}/approve`, data || {});
    } catch (error) {
      console.error('Error approving estimation:', error);
      throw error;
    }
  }

  /**
   * Reject estimation
   * @param id Estimation ID
   * @param data Rejection reason
   * @returns Rejected estimation
   */
  static async rejectEstimation(
    id: number,
    data: RejectEstimationRequest
  ): Promise<{
    estimation: ProjectEstimation;
    message: string;
  }> {
    try {
      return await ApiService.post(`/admin/estimations/${id}/reject`, data);
    } catch (error) {
      console.error('Error rejecting estimation:', error);
      throw error;
    }
  }

  /**
   * Convert approved estimation to project
   * @param id Estimation ID
   * @param data Optional conversion notes
   * @returns Created project
   */
  static async convertToProject(
    id: number,
    data?: ConvertToProjectRequest
  ): Promise<{
    estimation: ProjectEstimation;
    project: any; // Project type from ProjectService
    message: string;
  }> {
    try {
      return await ApiService.post(`/admin/estimations/${id}/convert-to-project`, data || {});
    } catch (error) {
      console.error('Error converting to project:', error);
      throw error;
    }
  }

  /**
   * Regenerate specification with different AI provider or settings
   * @param id Estimation ID
   * @param data Regeneration options
   * @returns Updated queue job info
   */
  static async regenerateSpecification(
    id: number,
    data?: RegenerateSpecificationRequest
  ): Promise<{
    estimation: ProjectEstimation;
    queue_job: any;
    message: string;
  }> {
    try {
      return await ApiService.post(`/admin/estimations/${id}/regenerate-spec`, data || {});
    } catch (error) {
      console.error('Error regenerating specification:', error);
      throw error;
    }
  }

  /**
   * Add note to estimation
   * @param id Estimation ID
   * @param data Note content and type
   * @returns Created note
   */
  static async addNote(
    id: number,
    data: AddNoteRequest
  ): Promise<{
    note: EstimationNote;
    message: string;
  }> {
    try {
      return await ApiService.post(`/admin/estimations/${id}/notes`, data);
    } catch (error) {
      console.error('Error adding note:', error);
      throw error;
    }
  }

  /**
   * Get queue status and statistics
   * @returns Queue metrics
   */
  static async getQueueStatus(): Promise<QueueStatusResponse> {
    try {
      return await ApiService.get('/admin/estimations/queue-status');
    } catch (error) {
      console.error('Error fetching queue status:', error);
      throw error;
    }
  }

  // --------------------------------------------------------------------------
  // UTILITY METHODS
  // --------------------------------------------------------------------------

  /**
   * Format estimated cost for display
   * @param cost Cost in USD
   * @returns Formatted string
   */
  static formatCost(cost?: number): string {
    if (!cost) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(cost);
  }

  /**
   * Format time duration
   * @param seconds Duration in seconds
   * @returns Human-readable duration
   */
  static formatDuration(seconds?: number): string {
    if (!seconds) return 'N/A';
    
    if (seconds < 60) {
      return `${Math.round(seconds)}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      return `${minutes}m`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    }
  }

  /**
   * Calculate clarification progress percentage
   * @param clarifications Array of clarifications
   * @returns Progress percentage (0-100)
   */
  static calculateClarificationProgress(clarifications: EstimationClarification[]): number {
    if (!clarifications || clarifications.length === 0) return 0;
    
    const required = clarifications.filter(c => c.is_required);
    if (required.length === 0) return 100;
    
    const answered = required.filter(c => c.is_answered);
    return Math.round((answered.length / required.length) * 100);
  }

  /**
   * Check if all required clarifications are answered
   * @param clarifications Array of clarifications
   * @returns True if all required questions answered
   */
  static areAllRequiredClarificationsAnswered(
    clarifications: EstimationClarification[]
  ): boolean {
    if (!clarifications || clarifications.length === 0) return false;
    
    const required = clarifications.filter(c => c.is_required);
    return required.every(c => c.is_answered);
  }

  /**
   * Get status badge color class
   * @param status Estimation status
   * @returns Tailwind color class
   */
  static getStatusColorClass(status: string): string {
    const colorMap: Record<string, string> = {
      'draft': 'bg-gray-100 text-gray-800',
      'submitted': 'bg-blue-100 text-blue-800',
      'clarification_needed': 'bg-orange-100 text-orange-800',
      'generating_specification': 'bg-purple-100 text-purple-800',
      'specification_complete': 'bg-cyan-100 text-cyan-800',
      'reviewed': 'bg-teal-100 text-teal-800',
      'approved': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800',
      'converted_to_project': 'bg-emerald-100 text-emerald-800',
    };
    
    return colorMap[status] || 'bg-gray-100 text-gray-800';
  }

  /**
   * Format date for display
   * @param dateString ISO date string
   * @returns Formatted date
   */
  static formatDate(dateString?: string): string {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  /**
   * Calculate days since date
   * @param dateString ISO date string
   * @returns Number of days
   */
  static daysSince(dateString?: string): number {
    if (!dateString) return 0;
    
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  }
}

export default EstimationService;
